import { Injectable } from '@angular/core';

/**
 * Stops browser password autofill app-wide: every password input becomes a CSS-masked text input,
 * so the browser does not offer saved credentials or "save password". No attribute is needed on
 * the inputs. Started once from AppComponent.
 *
 * Chrome remembers an input as a password field once it has EVER been type="password", even if it
 * is switched back to text afterwards. So converting fields after they are rendered is not enough:
 * the 'password' type has to be intercepted before it reaches the element. start() patches
 * HTMLInputElement.type and Element.setAttribute('type', ...) so that 'password' is written as
 * 'text' + CSS mask. This covers static type="password" (Angular renderer), matInput's
 * [type]="hide ? 'text' : 'password'" toggles and jQuery .attr('type', 'password').
 * A MutationObserver stays as a fallback for inputs created from HTML strings (innerHTML).
 *
 * Skipped: the authentication pages (#/authentication/...) and anything inside an element with
 * the data-allow-autofill attribute, so the login form keeps normal password-manager behaviour.
 * Browsers without -webkit-text-security support are left unchanged.
 */
@Injectable({ providedIn: 'root' })
export class NoAutofillGlobalService {

  private static readonly cssMaskSupported =
    typeof CSS !== 'undefined' && CSS.supports && CSS.supports('-webkit-text-security', 'disc');

  private static started = false;

  start(): void {
    if (!NoAutofillGlobalService.cssMaskSupported || NoAutofillGlobalService.started) {
      return;
    }
    NoAutofillGlobalService.started = true;

    this.patchInputType();

    document.querySelectorAll<HTMLInputElement>('input[type=password]').forEach(i => this.convert(i));

    // Fallback for inputs that become type="password" without going through the patched APIs.
    new MutationObserver(records => {
      for (const r of records) {
        if (r.type === 'attributes') {
          const el = r.target as HTMLInputElement;
          if (el.getAttribute('type') === 'password') {
            this.convert(el);
          }
        } else {
          r.addedNodes.forEach(n => {
            if (n instanceof HTMLInputElement) {
              if (n.getAttribute('type') === 'password') {
                this.convert(n);
              }
            } else if (n instanceof HTMLElement) {
              n.querySelectorAll<HTMLInputElement>('input[type=password]').forEach(i => this.convert(i));
            }
          });
        }
      }
    }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['type'] });

    // Do not let a masked value be copied out as plain text.
    const onCopy = (event: ClipboardEvent) => {
      const el = event.target;
      if (el instanceof HTMLInputElement && el.dataset.naMasked === 'true') {
        event.preventDefault();
      }
    };
    document.addEventListener('copy', onCopy, true);
    document.addEventListener('cut', onCopy, true);
  }

  private patchInputType(): void {
    const service = this;
    const typeDesc = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'type');
    if (!typeDesc || !typeDesc.get || !typeDesc.set) {
      return;
    }
    const nativeGetType = typeDesc.get;
    const nativeSetType = typeDesc.set;
    const nativeSetAttribute = Element.prototype.setAttribute;

    Object.defineProperty(HTMLInputElement.prototype, 'type', {
      configurable: true,
      enumerable: typeDesc.enumerable,
      get() {
        return nativeGetType.call(this);
      },
      set(value: string) {
        const isPassword = String(value).toLowerCase() === 'password';
        if (isPassword && !service.isExcluded(this)) {
          nativeSetType.call(this, 'text');
          service.mask(this);
        } else {
          nativeSetType.call(this, value);
          if (this.dataset.naMasked === 'true') {
            service.unmask(this);           // show/hide toggle switched to text
          }
        }
      }
    });

    Element.prototype.setAttribute = function (name: string, value: string) {
      if (this instanceof HTMLInputElement && String(name).toLowerCase() === 'type') {
        this.type = value;                  // goes through the patched setter above
        return;
      }
      return nativeSetAttribute.call(this, name, value);
    };
  }

  // Input created from an HTML string and already type="password": convert it now.
  private convert(el: HTMLInputElement): void {
    if (!this.isExcluded(el)) {
      el.type = 'password';                 // patched setter writes 'text' and masks
    }
  }

  private isExcluded(el: HTMLInputElement): boolean {
    return window.location.hash.indexOf('#/authentication') === 0 || !!el.closest('[data-allow-autofill]');
  }

  private mask(el: HTMLInputElement): void {
    el.setAttribute('autocomplete', 'new-password');
    el.setAttribute('autocorrect', 'off');
    el.setAttribute('autocapitalize', 'off');
    el.setAttribute('spellcheck', 'false');
    // Hints for password managers (LastPass, 1Password, Bitwarden) to skip this field.
    el.setAttribute('data-lpignore', 'true');
    el.setAttribute('data-1p-ignore', 'true');
    el.setAttribute('data-bwignore', 'true');
    el.style.setProperty('-webkit-text-security', 'disc');
    el.dataset.naMasked = 'true';
  }

  private unmask(el: HTMLInputElement): void {
    el.style.removeProperty('-webkit-text-security');
    el.dataset.naMasked = 'false';
  }
}
